/**
 * Die Farben der **Karte** — der verkleinerten Abbildung des Canvas
 * (CONTEXT.md, `.claude/tasks/die-karte-auf-dem-canvas.md`).
 *
 * Bewusst ohne React- und DOM-Bezug, wie die übrigen `utils/`: Die Zuordnung
 * soll ohne gerenderte Karte prüfbar sein.
 */

import { getCanvasNoteColor, getCanvasNoteColorHex } from './canvasNoteStyle'

/**
 * Wie weit die Farbe eines **Bereichs** auf der Karte ausgeblichen wird.
 *
 * Auf dem Canvas liegt ein Bereich UNTER allen Zetteln und trägt eine blassere
 * Fassung derselben Palette; auf der Karte wäre er sonst ein großer Fleck, der
 * die Zettel darauf erschlägt. Die Hälfte ist keine gemessene Zahl, sondern
 * eine gewählte: genug, dass ein gelber Zettel sich von einem gelben Bereich
 * abhebt, und wenig genug, dass die Familie noch erkennbar bleibt.
 */
const AREA_FADE = 0.5

/** Die Fläche, gegen die ausgeblichen wird — der Grund der Karte. */
const MAP_BACKGROUND = '#ffffff'

/** `#rgb` und `#rrggbb` zu drei Zahlen. `null`, wenn es keine Farbe ist. */
function readHex(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace(/^#/, '')
  const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

/**
 * Zwei Farben mischen — `amount` ist der Anteil der ZWEITEN.
 *
 * Linear im sRGB-Raum und nicht wahrnehmungsrichtig: Es geht um ein Ausbleichen
 * gegen Weiß auf wenigen Pixeln, nicht um eine Palette. Ein OKLCH-Mix wäre hier
 * Aufwand ohne sichtbaren Unterschied.
 */
export function mixHex(from: string, to: string, amount: number): string {
  const a = readHex(from)
  const b = readHex(to)
  if (!a || !b) return from
  const t = Math.min(1, Math.max(0, amount))
  const mixed = a.map((value, i) => Math.round(value + (b[i]! - value) * t))
  return `#${mixed.map(v => v.toString(16).padStart(2, '0')).join('')}`
}

/**
 * Die Farbe, mit der ein **Canvas-Element** auf der Karte steht.
 *
 * **Zettel bekommen ihr echtes `hex`** — dieselbe Farbe, die ihre Fläche auf
 * dem Canvas trägt. Genau daran erkennt man die Struktur eines Projekts aus
 * der Höhe: Wer in Gelb plant und in Grün abhakt, sieht das auf der Karte.
 *
 * **Bereiche werden ausgeblichen.** Sie sind groß und liegen unter allem; in
 * voller Sättigung wären sie auf der Karte der Hauptdarsteller, obwohl sie auf
 * dem Canvas der Hintergrund sind.
 */
export function canvasMapNodeColor(kind: string, colorKey: string | null): string {
  // Der **Text** hat keine Fläche — auf der Karte steht er als Strich in
  // seiner Tinte, die Standard-Tinte als Mittelgrau (ein `var()` kennt die
  // Karte nicht, sie malt auf ein eigenes Weiß).
  if (kind === 'text') return colorKey ? getCanvasNoteColor(colorKey).swatch : '#737373'
  const hex = getCanvasNoteColorHex(colorKey)
  return kind === 'area' ? mixHex(hex, MAP_BACKGROUND, AREA_FADE) : hex
}
